/**
 * AddressCorrectionCard - Card component for displaying a single address correction request
 *
 * SESSION 197: Address Correction Frontend Admin UI
 *
 * Features:
 * - Business name and user info
 * - Current address (strikethrough) vs requested address comparison
 * - Inline approve/reject forms for pending requests
 */
import React, { useState, useCallback } from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  Divider,
  Button,
  Textarea,
  Collapse,
  Box
} from '@mantine/core';
import {
  IconBuilding,
  IconUser,
  IconCalendar,
  IconCheck,
  IconX,
  IconMapPin
} from '@tabler/icons-react';
import { getStatusColor } from './AddressCorrectionReviewSection.types';
import type { AddressCorrectionCardProps } from './AddressCorrectionReviewSection.types';
import { parseAsUTC } from '@/infrastructure/utils/dateUtils';

/**
 * Format date string for display
 */
const formatDate = (dateString: string): string => {
  try {
    const date = parseAsUTC(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  } catch {
    return dateString;
  }
};

/**
 * AddressCorrectionCard Component
 *
 * Displays a single address correction request with inline actions
 */
export const AddressCorrectionCard: React.FC<AddressCorrectionCardProps> = ({
  correction,
  onApprove,
  onReject,
  actionsDisabled
}): React.ReactElement => {
  const [expandedAction, setExpandedAction] = useState<'approve' | 'reject' | null>(null);
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const statusColor = getStatusColor(correction.status);
  const isPending = correction.status === 'Pending';

  // Toggle approve form
  const handleToggleApprove = useCallback((): void => {
    setExpandedAction(expandedAction === 'approve' ? null : 'approve');
    setAdminNotes('');
  }, [expandedAction]);

  // Toggle reject form
  const handleToggleReject = useCallback((): void => {
    setExpandedAction(expandedAction === 'reject' ? null : 'reject');
    setRejectionReason('');
    setAdminNotes('');
  }, [expandedAction]);

  // Submit approve
  const handleApproveSubmit = useCallback(async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onApprove(correction, adminNotes || undefined);
      setExpandedAction(null);
      setAdminNotes('');
    } finally {
      setIsSubmitting(false);
    }
  }, [correction, adminNotes, onApprove]);

  // Submit reject
  const handleRejectSubmit = useCallback(async (): Promise<void> => {
    if (!rejectionReason.trim()) {
      return; // Require rejection reason
    }
    setIsSubmitting(true);
    try {
      await onReject(correction, rejectionReason, adminNotes || undefined);
      setExpandedAction(null);
      setRejectionReason('');
      setAdminNotes('');
    } finally {
      setIsSubmitting(false);
    }
  }, [correction, rejectionReason, adminNotes, onReject]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header Row: Status Badge | Requested Date */}
        <Group justify="space-between" wrap="nowrap">
          <Badge color={statusColor} variant="filled" size="lg">
            {correction.statusDisplay}
          </Badge>
          <Group gap="xs">
            <IconCalendar size={14} style={{ opacity: 0.6 }} />
            <Text size="xs" c="dimmed">
              Requested: {formatDate(correction.requestedAt)}
            </Text>
          </Group>
        </Group>

        {/* Business Name */}
        <Group gap="xs">
          <IconBuilding size={18} />
          <Text size="lg" fw={600}>
            {correction.businessName}
          </Text>
        </Group>

        <Divider />

        {/* Address Comparison */}
        <Stack gap="sm">
          {/* Current Address */}
          <Group gap="xs" align="flex-start">
            <IconMapPin size={16} style={{ opacity: 0.5, marginTop: 4 }} />
            <Stack gap={2}>
              <Text size="xs" fw={600} c="dimmed">
                Current Address:
              </Text>
              <Text
                size="sm"
                td="line-through"
                c="dimmed"
                style={{ opacity: 0.7 }}
              >
                {correction.currentAddress || 'No address on file'}
              </Text>
            </Stack>
          </Group>

          {/* Requested Address */}
          <Group gap="xs" align="flex-start">
            <IconMapPin size={16} style={{ color: 'var(--mantine-color-green-6)', marginTop: 4 }} />
            <Stack gap={2}>
              <Text size="xs" fw={600} c="green">
                Requested Address:
              </Text>
              <Text size="sm" fw={500} c="green">
                {correction.requestedAddress}
              </Text>
            </Stack>
          </Group>
        </Stack>

        <Divider />

        {/* User Info */}
        <Group gap="xs">
          <IconUser size={16} style={{ opacity: 0.6 }} />
          <Text size="sm" c="dimmed">
            Requested by: <Text span fw={500}>{correction.userName}</Text> ({correction.userEmail})
          </Text>
        </Group>

        {/* Review Info (if already reviewed) */}
        {correction.reviewedAt && (
          <Text size="xs" c="dimmed">
            Reviewed: {formatDate(correction.reviewedAt)}
            {correction.reviewedByUserName && ` by ${correction.reviewedByUserName}`}
          </Text>
        )}

        {/* Rejection Reason (if rejected) */}
        {correction.status === 'Rejected' && correction.rejectionReason && (
          <Box
            p="sm"
            style={{
              backgroundColor: 'var(--mantine-color-red-0)',
              borderRadius: '8px',
              border: '1px solid var(--mantine-color-red-3)'
            }}
          >
            <Text size="sm" c="red" fw={500}>
              Rejection Reason: {correction.rejectionReason}
            </Text>
          </Box>
        )}

        {/* Admin Notes (if any) */}
        {correction.adminNotes && (
          <Box
            p="sm"
            style={{
              backgroundColor: 'var(--mantine-color-gray-0)',
              borderRadius: '8px',
              border: '1px solid var(--mantine-color-gray-3)'
            }}
          >
            <Text size="sm" c="dimmed">
              Admin Notes: {correction.adminNotes}
            </Text>
          </Box>
        )}

        {/* Inline Actions (only for Pending) */}
        {isPending && (
          <>
            <Divider />

            {/* Action Buttons */}
            <Group gap="sm">
              <Button
                variant={expandedAction === 'approve' ? 'filled' : 'light'}
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={handleToggleApprove}
                disabled={actionsDisabled || isSubmitting}
              >
                Approve
              </Button>
              <Button
                variant={expandedAction === 'reject' ? 'filled' : 'light'}
                color="red"
                leftSection={<IconX size={16} />}
                onClick={handleToggleReject}
                disabled={actionsDisabled || isSubmitting}
              >
                Reject
              </Button>
            </Group>

            {/* Approve Form */}
            <Collapse in={expandedAction === 'approve'}>
              <Stack gap="sm" mt="sm">
                <Textarea
                  label="Admin Notes (optional)"
                  placeholder="Add any notes about this approval..."
                  value={adminNotes}
                  onChange={(e): void => setAdminNotes(e.target.value)}
                  rows={2}
                />
                <Group gap="sm">
                  <Button
                    color="green"
                    leftSection={<IconCheck size={16} />}
                    onClick={handleApproveSubmit}
                    loading={isSubmitting}
                    disabled={actionsDisabled}
                  >
                    Confirm Approve
                  </Button>
                  <Button
                    variant="subtle"
                    color="gray"
                    onClick={(): void => setExpandedAction(null)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </Group>
              </Stack>
            </Collapse>

            {/* Reject Form */}
            <Collapse in={expandedAction === 'reject'}>
              <Stack gap="sm" mt="sm">
                <Textarea
                  label="Rejection Reason"
                  placeholder="Explain why this address correction is being rejected..."
                  value={rejectionReason}
                  onChange={(e): void => setRejectionReason(e.target.value)}
                  required
                  rows={2}
                  error={expandedAction === 'reject' && !rejectionReason.trim() ? 'Rejection reason is required' : undefined}
                />
                <Textarea
                  label="Admin Notes (optional)"
                  placeholder="Add any additional notes..."
                  value={adminNotes}
                  onChange={(e): void => setAdminNotes(e.target.value)}
                  rows={2}
                />
                <Group gap="sm">
                  <Button
                    color="red"
                    leftSection={<IconX size={16} />}
                    onClick={handleRejectSubmit}
                    loading={isSubmitting}
                    disabled={actionsDisabled || !rejectionReason.trim()}
                  >
                    Confirm Reject
                  </Button>
                  <Button
                    variant="subtle"
                    color="gray"
                    onClick={(): void => setExpandedAction(null)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </Group>
              </Stack>
            </Collapse>
          </>
        )}
      </Stack>
    </Card>
  );
};
